/**
 * 71.8 (DECISIONS 369–375): the ground's look — the owner's picks from the surface sheet (design_sheets/surface_strata.ts),
 * on by default since the owner's GPU check (Oct 31: +0.5 ms a frame on the megaplanet, +0.2 on the Story), the Lab's
 * **Ground look** row (`?groundlook=today`) its off switch. Each world takes its pick: viridis H (the second
 * marks: the hatch), bruma E (painted tiles), ochrus E or H (the row's choice until the owner settles it in play),
 * sonora D (strokes), the rest R (the same reading surface by surface: 375). Presentation only — a shader chained onto
 * the battle planet's mesh and the fine patch, over the facets' own colours; no sim state, no BATTLE_VERSION.
 *
 * What it adds: per mesh corner, the facets round it welded (their colour, their materials' shares, their normal, a
 * curvature) — built once a battle, the touched corners again after a crater; per patch cell, the same read at its point
 * of the facet under it. A world compiles only its own materials and marks (SL_M<n>, SL_K<id>), so it pays for nothing
 * it does not draw, and loads only its own tiles (public/textures/ground/, baked by `npm run bake:groundtiles`).
 */
import * as THREE from 'three';
import type { PlanetState, GroundMaterial, Vec3 } from '../types';
import { GROUND_MATERIALS } from '../constants/ground';
import { PLANET_LAYERS } from '../constants/world';
import { facetMaterial } from '../core/groundMaterial';
import { meshWeld, type Weld } from '../core/materialRelief';
import { look, surfaceRadiusLook, getTerrainIndex, lastSurfaceTriangle, lastSurfaceU, lastSurfaceV, nearestSurfaceVertex } from '../core/surface';
import { SL_NOISE_GLSL, SL_TAKES_GLSL } from './surfaceLookGlsl';
import type { PlanetMeshData } from './renderer';

// ---- the switch: the Lab's Ground look row ----------------------------------------------------------------------------------

/** today (the palette as it is: the off switch), or the picks (the default) — with ochrus's E (`picks`) or its H (`picksH`). */
export type GroundLook = 'today' | 'picks' | 'picksH';
export const GROUND_LOOKS: readonly GroundLook[] = ['today', 'picks', 'picksH'];
let override: GroundLook | null = null;
/** The Lab's launch (null: the URL's `?groundlook=`, else the picks). */
export function setGroundLook(v: GroundLook | null): void { override = v; }
export function groundLook(): GroundLook {
  if (override !== null) return override;
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('groundlook') : null;
    return (GROUND_LOOKS as readonly string[]).includes(q ?? '') ? q as GroundLook : 'picks';
  } catch { return 'picks'; }
}

// ---- the picks ---------------------------------------------------------------------------------------------------------------

/** The takes the picks use: D strokes (level 5), E painted tiles (6), H the second marks and R the reading (8: a mark a surface). */
export type GroundTake = 'D' | 'E' | 'H' | 'R';
const LEVEL: Record<GroundTake, number> = { D: 5, E: 6, H: 8, R: 8 };
/** The owner's picks (372); every other archetype takes R (375). */
export const GROUND_PICKS: Readonly<Partial<Record<string, GroundTake>>> = { viridis: 'H', bruma: 'E', ochrus: 'E', sonora: 'D' };
export function pickOf(arch: string, gl: GroundLook = 'picks'): GroundTake {
  if (gl === 'picksH' && arch === 'ochrus') return 'H';
  return GROUND_PICKS[arch] ?? 'R';
}
/**
 * The marks (the shader's ids, slMarks3): soil 3 the hatch, 4 clods · rock 10 chips (strokes), 12 gravel with beds where
 * steep · sand 20 E's ripples, 21 D's crests · snow 31 D's scour, 32 E's drifts · ice 41 plates, 42 E's streaks · mud 51
 * E's ruts · ash 61 E's drifts · salt 71 E's ridges. H is round 3's second marks; R reads the picks surface by surface.
 */
const MARKS_H: Record<GroundMaterial, number> = { soil: 3, rock: 12, sand: 21, snow: 31, ice: 41, mud: 51, ash: 61, salt: 71, scree: 12 };
const MARKS_R: Record<GroundMaterial, number> = { soil: 3, rock: 10, sand: 20, snow: 32, ice: 42, mud: 51, ash: 61, salt: 71, scree: 10 };
/**
 * The material the look draws for material i (GROUND_MATERIALS' index): its own, but scree (74.13 step 4, loose rock) as rock —
 * rock's marks and tiles, its own lighter colour (the facet's, in the colour field). The shader holds eight materials.
 */
const ROCK = GROUND_MATERIALS.indexOf('rock');
export function lookMaterial(i: number): number { return GROUND_MATERIALS[i] === 'scree' ? ROCK : i; }
/** A take's mark for each material (GROUND_MATERIALS' order; −1: none — D and E draw each material their own way). */
export function marksOf(arch: string, take: GroundTake): number[] {
  if (take === 'D' || take === 'E') return GROUND_MATERIALS.map(() => -1);
  const base = take === 'H' ? MARKS_H : { ...MARKS_R, ...(arch === 'pallor' ? { soil: 4 } : {}) };   // (pallor's steppe: its clods, not a sward)
  return GROUND_MATERIALS.map(m => base[m]);
}
/** The tile layer each tile mark draws from (strokes draw none). */
const LAYER_OF_MARK: Readonly<Record<number, number>> = { 3: 9, 4: 10, 12: 11, 20: 2, 32: 3, 41: 13, 42: 4, 51: 5, 61: 6, 71: 7 };
/** The tile layers the picks can reach: E's eight (one a material) and the marks'. The bake writes these. */
export const GROUND_TILE_LAYERS: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 13];
/** The layers in the tiles' numbering (the sheet's: src/dev/groundTilePaint.ts). */
const TILE_LAYERS = 17;

/** What a world compiles and loads: its take, the materials it has (rock always: the slope rule shows it anywhere), each one's mark, its tiles. */
export interface LookPlan { take: GroundTake; level: number; mats: number[]; marks: number[]; layers: number[] }
export function lookPlan(arch: string, present: Iterable<number>, gl: GroundLook = 'picks'): LookPlan {
  const take = pickOf(arch, gl), level = LEVEL[take], marks = marksOf(arch, take);
  const mats = [...new Set([ROCK, ...[...present].map(lookMaterial)])].sort((a, b) => a - b);
  const layers = level === 6 ? mats.slice() : level === 8 ? [...new Set(mats.map(m => LAYER_OF_MARK[marks[m]]).filter(l => l !== undefined))].sort((a, b) => a - b) : [];
  return { take, level, mats, marks, layers };
}
/** The plan's defines: its materials (SL_M<n>), its marks (SL_K<id>) and SL_MK(m), material m's mark. */
export function lookDefines(plan: LookPlan): string {
  const mk = plan.mats.map(m => `(m) == ${m} ? ${plan.marks[m].toFixed(1)} : `).join('');
  return plan.mats.map(m => `#define SL_M${m}\n`).join('')
    + [...new Set(plan.mats.map(m => plan.marks[m]).filter(k => k >= 0))].map(k => `#define SL_K${k}\n`).join('')
    + `#define SL_MK(m) (${mk}-1.0)\n`;
}
const planKey = (plan: LookPlan) => `${plan.level}:${plan.mats.map(m => `${m}.${plan.marks[m]}`).join(',')}`;

// ---- the shader -----------------------------------------------------------------------------------------------------------------

/** A's light reads slopes this much steeper than they are (the shading only), so a gentle hill shows a sunny and a shaded side. */
export const SL_RELIEF_GAIN = 2.2;

const FRAG_PARS = /* glsl */`
uniform vec3 uCentre;
uniform float uPerson;
uniform vec3 uRock;
varying vec3 vPP;
varying float vCav;
${SL_NOISE_GLSL}
${SL_TAKES_GLSL}`;

/**
 * Chain the plan's look onto `material` (after its own hooks: the far lift, the patch's fade, the topo overlay): `coarse`
 * the planet's mesh (its normals the welded ones), else the fine patch (its own). The geometry carries aCol, aW0, aW1,
 * aCav (and aSmooth on the mesh).
 */
function applyLook(material: THREE.Material, plan: LookPlan, coarse: boolean, u: Record<string, THREE.IUniform>): void {
  const before = material.onBeforeCompile?.bind(material);
  const defs = `#define SL_LOOK ${plan.level}\n#define SL_LAYERS ${TILE_LAYERS}\n#define SL_RELIEF ${SL_RELIEF_GAIN.toFixed(2)}\n${coarse ? '#define SL_COARSE\n' : ''}${lookDefines(plan)}`;
  material.onBeforeCompile = (shader, renderer) => {
    before?.(shader, renderer);
    Object.assign(shader.uniforms, u);
    shader.vertexShader = defs + shader.vertexShader
      .replace('#include <common>', `#include <common>
uniform vec3 uCentre;
attribute float aCav;
attribute vec3 aCol;
attribute vec4 aW0;
attribute vec4 aW1;
varying vec3 vCol;
varying vec4 vW0;
varying vec4 vW1;
varying vec3 vPP;
varying float vCav;
#ifdef SL_COARSE
attribute vec3 aSmooth;
#endif`)
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
#ifdef SL_COARSE
  objectNormal = aSmooth;   // the facets' normals welded at the corner: the facets hidden (round 4: whole — 0.9 left a steep pair's edges)
#endif`)
      .replace('#include <project_vertex>', `#include <project_vertex>
  vPP = (modelMatrix * vec4(transformed, 1.0)).xyz - uCentre; vCav = aCav; vCol = aCol; vW0 = aW0; vW1 = aW1;`);
    shader.fragmentShader = defs + shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAG_PARS}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
{
  vec3 up = normalize(vPP), p = vPP / uPerson;
  vec3 nW = normalize((vec4(normal, 0.0) * viewMatrix).xyz);
  float slope = 1.0 - clamp(dot(nW, up), 0.0, 1.0);
  // the colour field: each corner its facets' colours welded, so the facets' one-colour plates run together
  vec3 c = vCol;
  // A: hollows darker, crests lighter; slopes eased toward the world's rock; the light reads the slopes SL_RELIEF × steeper
  c *= clamp(1.0 - 0.34 * vCav, 0.66, 1.26);
  c = mix(c, uRock, smoothstep(0.2, 0.62, slope) * 0.3);
  {
    vec3 tang = nW - up * dot(nW, up);
    vec3 nX = normalize(up * dot(nW, up) + tang * SL_RELIEF);
    normal = normalize((viewMatrix * vec4(nX, 0.0)).xyz);
  }
  // the take: where we are, what the rules read there, the two strongest materials blended by height, the marks
  {
    SlCtx x;
    x.p = p; x.up = up; x.dpx = dFdx(p); x.dpy = dFdy(p); x.px = max(length(fwidth(p)), 1e-4);
    x.slope = slope; x.cav = vCav; x.on = uRules; x.view = normalize(cameraPosition - vPP - uCentre);
    vec3 tn = nW - up * dot(nW, up);
    x.down = length(tn) > 1e-4 ? normalize(tn) : normalize(cross(up, vec3(0.0, 1.0, 0.3)));
    x.contour = normalize(cross(up, x.down));
    x.wind = normalize(SL_WIND - up * dot(SL_WIND, up));
    x.lean = uRules > 0.5 ? normalize(x.wind * 0.6 + x.down * min(slope * 5.0, 1.6)) : x.wind;
    x.alt = uRules > 0.5 ? clamp((length(vPP) - uAlt.x) / uAlt.y, -1.5, 1.5) : 0.0;
    x.moist = uRules > 0.5 ? clamp(0.5 + 0.45 * vCav - 0.3 * x.alt + 0.6 * (slFbm(p / 45.0) - 0.5), 0.0, 1.0) : 0.5;
    vec3 sT = uSunW - up * dot(uSunW, up);
    x.aspect = uRules > 0.5 && length(sT) > 1e-4 ? dot(x.down, normalize(sT)) * smoothstep(0.03, 0.22, slope) : 0.0;
    x.dry = uRules > 0.5 ? clamp(0.55 - x.moist + 0.3 * x.aspect + 0.2 * max(x.alt, 0.0), 0.0, 1.0) : 0.3;
    float wm[8];
    wm[0] = vW0.x; wm[1] = vW0.y; wm[2] = vW0.z; wm[3] = vW0.w; wm[4] = vW1.x; wm[5] = vW1.y; wm[6] = vW1.z; wm[7] = vW1.w;
    float expo = uRules > 0.5 ? smoothstep(0.38, 0.65, slope) : 0.0;
    wm[1] += expo * 1.2;   // on a steep face the rock shows through
    int ia = 0, ib = 0; float wa = -1.0, wb = -1.0;
    for (int k = 0; k < 8; k++) { float v = wm[k]; if (v > wa) { wb = wa; ib = ia; wa = v; ia = k; } else if (v > wb) { wb = v; ib = k; } }
    float ha, hb = 0.0, t = 0.0;
    vec3 ga = slTri(ia, x, ha), gb = ga;
    if (wb > 0.03) { gb = slTri(ib, x, hb); t = clamp(((wb + 0.5 * hb) - (wa + 0.5 * ha)) / 0.1 + 0.5, 0.0, 1.0); }
    c = mix(c, uRock, 0.45 * expo);
    c *= mix(ga, gb, t);
    slRulesTint(c, t > 0.5 ? ib : ia, x);
  }
  diffuseColor.rgb = c;
}`)
      .replace('#include <aomap_fragment>', `#include <aomap_fragment>
  reflectedLight.directDiffuse *= vec3(1.05, 1.0, 0.92);
  reflectedLight.indirectDiffuse *= vec3(0.9, 0.96, 1.1);`);
  };
  const key = material.customProgramCacheKey?.bind(material);
  material.customProgramCacheKey = () => `${key ? key() : ''}|surface-look-${coarse ? 'c' : 'p'}-${planKey(plan)}`;
  material.needsUpdate = true;
}

// ---- the tiles: baked files, a world loading only its own ---------------------------------------------------------------------

const TILE_DIR = 'textures/ground/';
interface TileMeta { px: number; size: number[]; mean: number[] }
let metaP: Promise<TileMeta> | null = null;
let warned = false;
/** The world's tile uniforms: a neutral one-texel stand-in until its layers load (the marks' value 1: the palette as it is). */
function tileUniforms(layers: number[]): { u: Record<string, THREE.IUniform>; dispose: () => void } {
  const stand = new THREE.DataArrayTexture(new Uint8Array([128, 128, 128, 255]), 1, 1, 1);
  stand.needsUpdate = true;
  const u = { uTiles: { value: stand as THREE.DataArrayTexture }, uLSize: { value: new Array(TILE_LAYERS).fill(4) }, uLMean: { value: new Array(TILE_LAYERS).fill(128 / 255) }, uLIdx: { value: new Array(TILE_LAYERS).fill(0) } };
  let disposed = false;
  if (layers.length && typeof fetch !== 'undefined' && typeof document !== 'undefined') {
    (metaP ??= fetch(`${TILE_DIR}tiles.json`).then(r => r.json() as Promise<TileMeta>)).then(async meta => {
      const N = meta.px, data = new Uint8Array(N * N * 4 * layers.length);
      const cv = document.createElement('canvas'); cv.width = cv.height = N;
      const g = cv.getContext('2d', { willReadFrequently: true })!;
      for (let s = 0; s < layers.length; s++) {
        const blob = await (await fetch(`${TILE_DIR}tile-${String(layers[s]).padStart(2, '0')}.png`)).blob();
        const bmp = await createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
        g.clearRect(0, 0, N, N); g.drawImage(bmp, 0, 0); bmp.close();
        data.set(g.getImageData(0, 0, N, N).data, s * N * N * 4);
      }
      if (disposed) return;
      const tex = new THREE.DataArrayTexture(data, N, N, layers.length);
      tex.format = THREE.RGBAFormat; tex.type = THREE.UnsignedByteType; tex.colorSpace = THREE.NoColorSpace;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.anisotropy = 8;
      tex.generateMipmaps = true; tex.minFilter = THREE.LinearMipmapLinearFilter; tex.magFilter = THREE.LinearFilter;
      tex.needsUpdate = true;
      for (let L = 0; L < TILE_LAYERS; L++) u.uLSize.value[L] = meta.size[L];
      layers.forEach((L, s) => { u.uLIdx.value[L] = s; u.uLMean.value[L] = meta.mean[L]; });
      u.uTiles.value = tex;
      stand.dispose();
    }).catch(e => { if (!warned) { warned = true; console.warn('ground look: the tiles did not load (the marks drawn flat)', e); } });
  }
  return { u, dispose: () => { disposed = true; u.uTiles.value.dispose(); } };
}

// ---- the far mesh: its corners welded ------------------------------------------------------------------------------------------

/** A planet's look: its plan, its uniforms (shared with the patch), the weld and the per-entry arrays the patch reads. */
export interface SurfaceLookState {
  planetId: number; plan: LookPlan; uniforms: Record<string, THREE.IUniform>; weld: Weld;
  aCol: Float32Array; aW0: Float32Array; aW1: Float32Array; aCav: Float32Array; aSmooth: Float32Array;
  /** |curvature|'s 90th percentile at the build (the scale every corner's is read against) */
  p90: number;
  /** milliseconds: the build's (the weld, the shares, the shape, the colours) */
  buildMs: number;
  dispose: () => void;
}

/** Whether this battle draws the picks, and on which planet: the action clock at human scale, the row not today. */
export function lookPlanetIndex(state: { config: { clock?: string } }, battleIndex: number, k: number): number | null {
  return state.config.clock === 'action' && k < 1 && groundLook() !== 'today' ? battleIndex : null;
}

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3(), _e1 = new THREE.Vector3(), _e2 = new THREE.Vector3(), _n = new THREE.Vector3();

/** Vertex v's welded normal and curvature (raw) from the facets round it — the mesh's live positions and heights. */
function vertexShape(w: Weld, pos: ArrayLike<number>, hm: ArrayLike<number>, v: number, nOut: THREE.Vector3): number {
  nOut.set(0, 0, 0);
  let hsum = 0, hcnt = 0, hown = 0, elen = 0;
  for (let q = w.facetsAt[v]; q < w.facetsAt[v + 1]; q++) {
    const t = w.facets[q], i0 = t * 3;
    _a.set(pos[i0 * 3], pos[i0 * 3 + 1], pos[i0 * 3 + 2]); _b.set(pos[i0 * 3 + 3], pos[i0 * 3 + 4], pos[i0 * 3 + 5]); _c.set(pos[i0 * 3 + 6], pos[i0 * 3 + 7], pos[i0 * 3 + 8]);
    _e1.subVectors(_b, _a); _e2.subVectors(_c, _a);
    nOut.add(_n.crossVectors(_e1, _e2));   // (area-weighted)
    const k = w.vertexOf[i0] === v ? 0 : w.vertexOf[i0 + 1] === v ? 1 : 2;
    hown = hm[i0 + k];
    hsum += hm[i0 + (k + 1) % 3] + hm[i0 + (k + 2) % 3]; hcnt += 2;
    elen += k === 0 ? _e1.length() : k === 1 ? _b.distanceTo(_c) : _e2.length();   // (the sheet's: one edge a facet)
  }
  nOut.normalize();
  return hcnt ? (hsum / hcnt - hown) / (elen / (hcnt / 2) || 1) : 0;
}

/**
 * Build the look on the battle planet's mesh: the corners welded (the facets' colour, their materials' shares as made, the
 * normal, the curvature), the world's plan compiled onto its material. Once a battle (its cost on the meshData: buildMs).
 */
export function attachSurfaceLook(meshData: PlanetMeshData, planet: PlanetState, opts: { person: number; sun: THREE.Vector3; gl?: GroundLook }): SurfaceLookState {
  const t0 = performance.now();
  const geo = meshData.mesh.geometry as THREE.BufferGeometry;
  const pos = (geo.getAttribute('position') as THREE.BufferAttribute).array as Float32Array;
  const n = planet.heightmap.length, w = meshWeld(planet.basePositions), V = w.facetsAt.length - 1;
  // each facet's material as made (a dug facet's material is its stratum's; the place is the ground as made)
  const made: PlanetState = { ...planet, heightmap: planet.initialHeightmap ?? planet.heightmap };
  const facetMat = new Uint8Array(n / 3), present = new Set<number>();
  for (let t = 0; t < n / 3; t++) { const m = lookMaterial(GROUND_MATERIALS.indexOf(facetMaterial(made, t))); facetMat[t] = m; present.add(m); }
  const arch = planet.palette.name === 'earth' ? 'earth' : planet.archetype;
  const plan = lookPlan(arch, present, opts.gl ?? groundLook());
  // each corner its facets' materials' shares (two vec4s)
  const aW0 = new Float32Array(n * 4), aW1 = new Float32Array(n * 4), share = new Float32Array(8);
  for (let v = 0; v < V; v++) {
    share.fill(0);
    const from = w.facetsAt[v], to = w.facetsAt[v + 1];
    for (let q = from; q < to; q++) share[facetMat[w.facets[q]]] += 1 / (to - from);
    for (let q = from; q < to; q++) {
      const t = w.facets[q], i = t * 3 + (w.vertexOf[t * 3] === v ? 0 : w.vertexOf[t * 3 + 1] === v ? 1 : 2);
      for (let k = 0; k < 4; k++) { aW0[i * 4 + k] = share[k]; aW1[i * 4 + k] = share[4 + k]; }
    }
  }
  // the shape: each corner's welded normal and curvature (read against the 90th percentile of |curvature|)
  const cav = new Float32Array(V), nrm = new Float32Array(V * 3), hm = planet.heightmap;
  for (let v = 0; v < V; v++) { cav[v] = vertexShape(w, pos, hm, v, _n); nrm[v * 3] = _n.x; nrm[v * 3 + 1] = _n.y; nrm[v * 3 + 2] = _n.z; }
  const sorted = Float32Array.from(cav, Math.abs).sort(), p90 = sorted[Math.floor(sorted.length * 0.9)] || 1;
  const aSmooth = new Float32Array(n * 3), aCav = new Float32Array(n), aCol = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const v = w.vertexOf[i];
    aSmooth[i * 3] = nrm[v * 3]; aSmooth[i * 3 + 1] = nrm[v * 3 + 1]; aSmooth[i * 3 + 2] = nrm[v * 3 + 2];
    aCav[i] = Math.max(-1.6, Math.min(1.6, cav[v] / p90));
  }
  geo.setAttribute('aSmooth', new THREE.BufferAttribute(aSmooth, 3));
  geo.setAttribute('aCav', new THREE.BufferAttribute(aCav, 1));
  geo.setAttribute('aCol', new THREE.BufferAttribute(aCol, 3));
  geo.setAttribute('aW0', new THREE.BufferAttribute(aW0, 4));
  geo.setAttribute('aW1', new THREE.BufferAttribute(aW1, 4));
  weldColours(w, (geo.getAttribute('color') as THREE.BufferAttribute).array as Float32Array, aCol);
  // the uniforms (the patch takes the same): the centre, a person, the world's rock, the sun (live), the altitude's reference
  const radii = Float32Array.from(hm).sort();
  const alt = new THREE.Vector2(radii[Math.floor(radii.length / 2)], Math.max(1e-6, (radii[Math.floor(radii.length * 0.95)] - radii[Math.floor(radii.length * 0.05)]) / 2));
  const rock = new THREE.Color((PLANET_LAYERS[arch as keyof typeof PLANET_LAYERS] ?? PLANET_LAYERS.thalia).rock);
  const tiles = tileUniforms(plan.layers);
  const uniforms: Record<string, THREE.IUniform> = {
    uCentre: { value: meshData.mesh.position }, uPerson: { value: opts.person }, uRock: { value: new THREE.Vector3(rock.r, rock.g, rock.b) },
    uRules: { value: 1 }, uSunW: { value: opts.sun }, uAlt: { value: alt }, ...tiles.u,
  };
  const mat = meshData.mesh.material as THREE.MeshLambertMaterial;
  mat.flatShading = false;   // (lit by the welded normals: the facets hidden)
  applyLook(mat, plan, true, uniforms);
  const state: SurfaceLookState = { planetId: planet.id, plan, uniforms, weld: w, aCol, aW0, aW1, aCav, aSmooth, p90, buildMs: performance.now() - t0, dispose: tiles.dispose };
  meshData.look = state;
  states.set(planet.id, state);
  return state;
}

/** The look's per-planet states (the patch reads its planet's). */
const states = new Map<number, SurfaceLookState>();
export function surfaceLookOf(planetId: number): SurfaceLookState | null { return states.get(planetId) ?? null; }
export function disposeSurfaceLook(meshData: PlanetMeshData): void {
  const s = meshData.look;
  if (!s) return;
  s.dispose();
  if (states.get(s.planetId) === s) states.delete(s.planetId);
  meshData.look = undefined;
}

/** Each corner's colour: the mean of its facets' live colours (the mesh's colour attribute: a crater's, the fog's). */
function weldColours(w: Weld, col: Float32Array, aCol: Float32Array): void {
  const V = w.facetsAt.length - 1, sum = new Float32Array(V * 3), n = w.vertexOf.length;
  for (let i = 0; i < n; i++) { const v = w.vertexOf[i] * 3; sum[v] += col[i * 3]; sum[v + 1] += col[i * 3 + 1]; sum[v + 2] += col[i * 3 + 2]; }
  for (let i = 0; i < n; i++) {
    const v = w.vertexOf[i], c = w.facetsAt[v + 1] - w.facetsAt[v] || 1;
    aCol[i * 3] = sum[v * 3] / c; aCol[i * 3 + 1] = sum[v * 3 + 1] / c; aCol[i * 3 + 2] = sum[v * 3 + 2] / c;
  }
}

/** The mesh's colours changed (a crater's, the fog's): the corners' welded colours again (a pass over the entries). */
export function refreshSurfaceLookColours(meshData: PlanetMeshData): void {
  const s = meshData.look;
  if (!s) return;
  const geo = meshData.mesh.geometry as THREE.BufferGeometry;
  weldColours(s.weld, (geo.getAttribute('color') as THREE.BufferAttribute).array as Float32Array, s.aCol);
  (geo.getAttribute('aCol') as THREE.BufferAttribute).needsUpdate = true;
}

/**
 * The mesh's heights changed (`modified`: the entries moved, as updatePlanetMesh has them; all when absent): the welded
 * normal and curvature again for every corner of a facet round a moved one (their shape reads the moved heights).
 */
export function refreshSurfaceLookShape(meshData: PlanetMeshData, planet: PlanetState, modified?: ArrayLike<number>): void {
  const s = meshData.look;
  if (!s) return;
  const geo = meshData.mesh.geometry as THREE.BufferGeometry, w = s.weld, V = w.facetsAt.length - 1;
  const pos = (geo.getAttribute('position') as THREE.BufferAttribute).array as Float32Array, hm = planet.heightmap;
  let verts: Iterable<number>;
  if (!modified || modified.length === 0) verts = Array.from({ length: V }, (_, v) => v);
  else {
    const mark = new Uint8Array(V), list: number[] = [];
    for (let k = 0; k < modified.length; k++) {
      const v0 = w.vertexOf[modified[k]];
      for (let q = w.facetsAt[v0]; q < w.facetsAt[v0 + 1]; q++) {
        const t = w.facets[q];
        for (let c = 0; c < 3; c++) { const v = w.vertexOf[t * 3 + c]; if (!mark[v]) { mark[v] = 1; list.push(v); } }
      }
    }
    verts = list;
  }
  let lo = Infinity, hi = -1;
  for (const v of verts) {
    const cv = Math.max(-1.6, Math.min(1.6, vertexShape(w, pos, hm, v, _n) / s.p90));
    for (let q = w.facetsAt[v]; q < w.facetsAt[v + 1]; q++) {
      const t = w.facets[q], i = t * 3 + (w.vertexOf[t * 3] === v ? 0 : w.vertexOf[t * 3 + 1] === v ? 1 : 2);
      s.aSmooth[i * 3] = _n.x; s.aSmooth[i * 3 + 1] = _n.y; s.aSmooth[i * 3 + 2] = _n.z; s.aCav[i] = cv;
      if (i < lo) lo = i; if (i > hi) hi = i;
    }
  }
  if (hi < 0) return;
  for (const [name, size] of [['aSmooth', 3], ['aCav', 1]] as const) {
    const attr = geo.getAttribute(name) as THREE.BufferAttribute;
    attr.addUpdateRange(lo * size, (hi - lo + 1) * size);
    attr.needsUpdate = true;
  }
}

// ---- the fine patch: each cell the facet's welded values at its point ----------------------------------------------------------

/**
 * A patch's look arrays, one entry a cell (the patch owns and uploads them). `nrm`: the welded normal under the cell — the
 * base the patch builds its normals on with the look on (the mesh's facets' own normals are flat a facet, and the look's
 * relief gain draws each facet edge as a line of light).
 */
export interface PatchLookCells { col: Float32Array; w0: Float32Array; w1: Float32Array; cav: Float32Array; nrm: Float32Array }

/**
 * Cell k's values at direction `dir` on `planet`: the facet under it (the coarse lookup's hit) and its corners' welded
 * values by the point's barycentric weights — so the colour and the materials run across the facets' edges as on the mesh.
 */
export function surfaceLookCell(s: SurfaceLookState, planet: PlanetState, dir: Vec3, out: PatchLookCells, k: number): void {
  look[0] = dir.x; look[1] = dir.y; look[2] = dir.z; look[3] = planet.radius;
  surfaceRadiusLook(getTerrainIndex(planet.basePositions), planet.heightmap, planet.basePositions);
  const t = lastSurfaceTriangle();
  let e0: number, e1 = 0, e2 = 0, wa = 1, wb = 0, wc = 0;
  if (t >= 0) {
    wb = Math.min(1, Math.max(0, lastSurfaceU())); wc = Math.min(1, Math.max(0, lastSurfaceV())); wa = Math.max(0, 1 - wb - wc);
    e0 = t * 3; e1 = e0 + 1; e2 = e0 + 2;
  } else e0 = nearestSurfaceVertex(planet, dir);
  for (let q = 0; q < 3; q++) out.col[k * 3 + q] = wa * s.aCol[e0 * 3 + q] + wb * s.aCol[e1 * 3 + q] + wc * s.aCol[e2 * 3 + q];
  for (let q = 0; q < 4; q++) {
    out.w0[k * 4 + q] = wa * s.aW0[e0 * 4 + q] + wb * s.aW0[e1 * 4 + q] + wc * s.aW0[e2 * 4 + q];
    out.w1[k * 4 + q] = wa * s.aW1[e0 * 4 + q] + wb * s.aW1[e1 * 4 + q] + wc * s.aW1[e2 * 4 + q];
  }
  out.cav[k] = wa * s.aCav[e0] + wb * s.aCav[e1] + wc * s.aCav[e2];
  let nx = wa * s.aSmooth[e0 * 3] + wb * s.aSmooth[e1 * 3] + wc * s.aSmooth[e2 * 3];
  let ny = wa * s.aSmooth[e0 * 3 + 1] + wb * s.aSmooth[e1 * 3 + 1] + wc * s.aSmooth[e2 * 3 + 1];
  let nz = wa * s.aSmooth[e0 * 3 + 2] + wb * s.aSmooth[e1 * 3 + 2] + wc * s.aSmooth[e2 * 3 + 2];
  const l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
  nx /= l; ny /= l; nz /= l;
  out.nrm[k * 3] = nx; out.nrm[k * 3 + 1] = ny; out.nrm[k * 3 + 2] = nz;
}

/** The patch's material takes its planet's look (its own normals; the planet's uniforms). */
export function applySurfaceLookToPatch(material: THREE.Material, s: SurfaceLookState): void {
  applyLook(material, s.plan, false, s.uniforms);
}
